// src/pages/DarkWeb.jsx - Dark Web Exposures Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert, Tooltip, IconButton
} from '@mui/material';
import {
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Sync as SyncIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import { darkWebApi, clientsApi } from '../services/api';
import { toast } from 'react-toastify';

const maskPassword = (pw) => {
  if (!pw) return null;
  if (pw.length <= 2) return '••••';
  return pw[0] + '•'.repeat(Math.min(pw.length - 2, 6)) + pw[pw.length - 1];
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const PAGE_SIZE = 50;

const DarkWeb = () => {
  const [exposures, setExposures]     = useState([]);
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterClient, setFilterClient] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [syncing, setSyncing]         = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  const loadExposures = useCallback(async (pg = page) => {
    try {
      setLoading(true);
      const params = { page: pg, page_size: PAGE_SIZE };
      if (filterClient)   params.client_id = filterClient;
      if (filterSeverity) params.severity   = filterSeverity;
      const response = await darkWebApi.list(params);
      const data = response.data;
      setExposures(data.exposures || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setPage(pg);
    } catch (err) {
      toast.error('Failed to load dark web exposures');
      setExposures([]);
    } finally {
      setLoading(false);
    }
  }, [filterClient, filterSeverity, page]);

  useEffect(() => {
    loadExposures(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClient, filterSeverity]);

  useEffect(() => {
    clientsApi.getAll().then(r => {
      const data = Array.isArray(r.data) ? r.data : (r.data?.clients || r.data?.items || []);
      setClients(data);
    }).catch(() => setClients([]));
  }, []);

  const handleSync = async () => {
    if (!window.confirm('Run an incremental sync to pull new dark web exposures from Blackpoint?')) return;
    setSyncing(true);
    try {
      const r = await darkWebApi.sync(false);
      const d = r.data;
      toast.success(`Sync complete — ${d.inserted} new exposures, ${d.tickets_created} CW tickets created`);
      loadExposures(1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!filterClient) {
      toast.error('Please select a client to generate a report');
      return;
    }
    setGeneratingReport(true);
    try {
      await darkWebApi.downloadReport(filterClient);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Report generation failed');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Counts for summary cards
  const pwExposedCount = exposures.filter(e => e.password).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Dark Web Exposures
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total.toLocaleString()} total exposure{total !== 1 ? 's' : ''} sourced from Blackpoint CompassOne
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
            sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
          >
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadExposures(1)}
            sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary chips */}
      {!loading && total > 0 && pwExposedCount > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Chip
            icon={<LockOpenIcon sx={{ fontSize: '1rem !important' }} />}
            label={`${pwExposedCount} with exposed password`}
            sx={{ bgcolor: '#FADBD8', color: '#C0392B', fontWeight: 700 }}
          />
        </Box>
      )}

      {/* Filters + Generate Report */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Filter by Client</InputLabel>
              <Select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                label="Filter by Client"
              >
                <MenuItem value="">All Clients</MenuItem>
                {clients.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              startIcon={generatingReport ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
              onClick={handleGenerateReport}
              disabled={generatingReport || !filterClient}
              sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' }, minWidth: 180 }}
              title={!filterClient ? 'Select a client first' : 'Generate PDF report for selected client'}
            >
              {generatingReport ? 'Generating…' : 'Generate Report'}
            </Button>
          </Box>
          {!filterClient && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Select a client to enable report generation
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : exposures.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <SecurityIcon sx={{ fontSize: 64, color: '#bdc3c7', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No dark web exposures found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {filterClient || filterSeverity
                ? 'Try adjusting the filters above'
                : 'Run a sync to pull data from Blackpoint CompassOne'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#6B3FA0' }}>
                  {['Date Detected', 'Date Added', 'Client', 'Breach', 'Domain', 'Email / Username', 'Password'].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {exposures.map((exp, idx) => (
                  <TableRow
                    key={exp.id}
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'white' : '#F8F9FA',
                      '&:hover': { bgcolor: '#F3EEFA' },
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {fmtDate(exp.bp_created_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {fmtDate(exp.synced_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {exp.client_name || (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          {exp.bp_tenant_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', maxWidth: 220 }}>
                      {exp.breach_name || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {exp.impacted_domain || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {exp.email || exp.username || '—'}
                      {exp.email && exp.username && exp.email !== exp.username && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {exp.username}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem' }}>
                      {exp.password ? (
                        <Tooltip title={`Hash/type: ${exp.password_type || 'unknown'}`} arrow>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LockOpenIcon sx={{ fontSize: '0.9rem', color: '#C0392B' }} />
                            <span style={{ color: '#C0392B', fontWeight: 600 }}>
                              {maskPassword(exp.password)}
                            </span>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#7f8c8d' }}>
                          <LockIcon sx={{ fontSize: '0.9rem' }} />
                          <span>Not exposed</span>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={page <= 1}
                onClick={() => loadExposures(page - 1)}
                sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
              >
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages} ({total.toLocaleString()} total)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={page >= totalPages}
                onClick={() => loadExposures(page + 1)}
                sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
              >
                Next
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DarkWeb;
